/* eslint-disable react/jsx-max-depth */
import React, { useState, useMemo } from 'react';

import {
  $createParagraphNode,
  $getSelection,
  $isRangeSelection,
  ElementNode,
  LexicalEditor,
} from 'lexical';
import { INSERT_TABLE_COMMAND } from '@lexical/table';

import { useValidationService } from '../../../../../services/hooks';
import { Dialog, DialogContent } from '../../../../../Dialog';
import { Button } from '../../../../../Button';
import { TextInput } from '../../../../../input';
import { Grid } from '../../../../../Grid';
import { Box } from '../../../../../Box';
import { useForm } from '../../hooks';

interface TableDialogProps {
  editor: LexicalEditor;
  open: boolean;
  onClose: () => void;
}

interface TableDialogForm {
  rows: string;
  columns: string;
}

const MAX_ROWS_AND_COLUMNS_VALUE = 10;
const MIN_ROWS_AND_COLUMNS_VALUE = 0;

export const TableDialog = ({ editor, open, onClose }: TableDialogProps) => {
  const [rows, setRows] = useState('');
  const [columns, setColumns] = useState('');
  const validator = useValidationService();

  const allValues = useMemo(() => {
    return {
      rows,
      columns,
    };
  }, [rows, columns]);

  const fieldValidators = useMemo(() => {
    return {
      rows: [
        validator.required<TableDialogForm, 'rows'>('Обязательное поле'),
        validator.maxValue<TableDialogForm, 'rows'>(
          MAX_ROWS_AND_COLUMNS_VALUE,
          'Значение от 1 до 10',
        ),
        validator.minValue<TableDialogForm, 'rows'>(
          MIN_ROWS_AND_COLUMNS_VALUE,
          'Значение от 1 до 10',
        ),
      ],
      columns: [
        validator.required<TableDialogForm, 'columns'>('Обязательное поле'),
        validator.maxValue<TableDialogForm, 'columns'>(
          MAX_ROWS_AND_COLUMNS_VALUE,
          'Значение от 1 до 10',
        ),
        validator.minValue<TableDialogForm, 'columns'>(
          MIN_ROWS_AND_COLUMNS_VALUE,
          'Значение от 1 до 10',
        ),
      ],
    };
  }, [validator]);

  const {
    errors,
    touched,
    validateField,
    hasValidationErrors,
    showError,
    handleBlur,
  } = useForm<TableDialogForm>(allValues, fieldValidators);

  const handleChangeRowsInput = async (value?: string) => {
    setRows(() => value || '');
    await validateField('rows', value);
    showError('rows');
  };

  const handleChangeColumnsInput = async (value?: string) => {
    setColumns(() => value || '');
    await validateField('columns', value);
    showError('columns');
  };

  const onSubmit = () => {
    editor.update(() => {
      editor.dispatchCommand(INSERT_TABLE_COMMAND, {
        columns,
        rows,
      });

      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const anchorNode = selection.anchor.getNode();
        let tableNode = anchorNode;

        while (tableNode && tableNode.getType() !== 'table') {
          tableNode = tableNode.getParent() as ElementNode;
        }

        if (tableNode) {
          const paragraphNode = $createParagraphNode();
          const sibling = tableNode.getNextSibling();

          if (sibling) {
            sibling.replace(paragraphNode);

            return;
          }

          tableNode.insertAfter(paragraphNode);
        }
      }
    });

    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <form onSubmit={onSubmit}>
        <DialogContent
          title="Добавление таблицы"
          onClose={onClose}
          actions={
            <>
              <Button
                size="large"
                variant="tertiaryLight"
                type="button"
                onClick={onClose}
              >
                Отменить
              </Button>
              <Button
                disabled={hasValidationErrors}
                size="large"
                variant="primary"
                type="submit"
              >
                Добавить
              </Button>
            </>
          }
        >
          <Grid container spacing="24">
            <Grid item xs={6}>
              <Box display="flex" flexDirection="column">
                <TextInput
                  type="number"
                  label="Строки"
                  required
                  placeholder="Количество строк"
                  value={rows}
                  onChange={handleChangeRowsInput}
                  name="rows"
                  error={touched.rows && !!errors.rows}
                  helperText={
                    (touched.rows && errors.rows) || 'Значение от 1 до 10'
                  }
                  onBlur={handleBlur('rows')}
                />
              </Box>
            </Grid>

            <Grid item xs={6}>
              <Box display="flex" flexDirection="column">
                <TextInput
                  type="number"
                  label="Столбцы"
                  required
                  placeholder="Количество столбцов"
                  value={columns}
                  onChange={handleChangeColumnsInput}
                  name="columns"
                  error={touched.columns && !!errors.columns}
                  helperText={
                    (touched.columns && errors.columns) || 'Значение от 1 до 10'
                  }
                  onBlur={handleBlur('columns')}
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
      </form>
    </Dialog>
  );
};
